/**
 * A fila da avaliadora (`TP-065`, `NR-196`): as avaliações acionadas, com o prazo do aceite.
 *
 * É o aviso à coordenação (`D6`): não existe canal de envio (é o `NR-27`), então o que dá para entregar é a superfície
 * onde a coordenação já entra. Os vencidos vêm no topo, e para o papel `coordenacao` a tela abre filtrada neles.
 *
 * A avaliadora não é usuária (`EVD-038`): não há fila "da Fulana". O aceite é registrado pela captação, no passo do
 * Caso, e daqui se vai ao Caso.
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDoRoteador, useSearchParams } from 'react-router-dom';

import { formatarDataHora } from '../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';

import {
  ROTULO_DA_SITUACAO,
  ROTULO_DO_TURNO,
  filaDaAvaliadora,
  type SituacaoDoAcionamento,
} from '../../shared/filaDaAvaliadora/acionamento';
import { textoDoPrazo } from '../../shared/filaDaAvaliadora/textos';

const FILTROS: readonly SituacaoDoAcionamento[] = ['aceite_vencido', 'aguardando_aceite', 'aceita'];

/** O filtro da URL; sem ele, `coordenacao` abre nos vencidos e os outros papéis em todas. */
function filtroInicial(bruto: string | null, papel: string | undefined): SituacaoDoAcionamento | null {
  if (bruto === 'todas') return null;
  if (bruto !== null && (FILTROS as readonly string[]).includes(bruto)) return bruto as SituacaoDoAcionamento;
  return papel === 'coordenacao' ? 'aceite_vencido' : null;
}

const COR_DA_SITUACAO: Record<SituacaoDoAcionamento, 'error' | 'warning' | 'success'> = {
  aceite_vencido: 'error',
  aguardando_aceite: 'warning',
  aceita: 'success',
};

/** `papel` vem da sessão, pela rota: uma feature não lê a identidade de outra. */
export function FilaDaAvaliadora({ papel }: { papel: string | undefined }): React.JSX.Element {
  const [busca, definirBusca] = useSearchParams();
  const situacao = filtroInicial(busca.get('situacao'), papel);
  const fila = useQuery(filaDaAvaliadora(situacao));

  const escolher = (valor: SituacaoDoAcionamento | 'todas'): void => {
    definirBusca(
      (atual) => {
        const proxima = new URLSearchParams(atual);
        proxima.set('situacao', valor);
        return proxima;
      },
      { replace: true },
    );
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Fila da avaliação</Typography>
        <Typography variant="body2" color="text.secondary">
          As avaliações acionadas e o prazo para a avaliadora aceitar. O aceite, com o turno da visita, é registrado no
          passo do Caso. Os vencidos ficam no topo.
        </Typography>
      </Box>

      <Tabs
        value={situacao ?? 'todas'}
        onChange={(_, valor: SituacaoDoAcionamento | 'todas') => {
          escolher(valor);
        }}
        aria-label="Filtrar a fila pela situação"
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab
          value="aceite_vencido"
          label={
            fila.data !== undefined && fila.data.vencidos > 0
              ? `Vencidos (${String(fila.data.vencidos)})`
              : 'Vencidos'
          }
        />
        <Tab value="aguardando_aceite" label="Aguardando aceite" />
        <Tab value="aceita" label="Aceitas" />
        <Tab value="todas" label="Todas" />
      </Tabs>

      <Paper variant="outlined">
        {fila.isPending ? (
          <EstadoDeCarregamento linhas={5} />
        ) : fila.isError ? (
          <EstadoDeErro
            erro={fila.error}
            contexto="Não foi possível carregar a fila da avaliação"
            aoReprocessar={() => {
              void fila.refetch();
            }}
          />
        ) : fila.data.itens.length === 0 ? (
          <EstadoVazio
            titulo={situacao === 'aceite_vencido' ? 'Nenhum aceite vencido' : 'Nada nesta fila'}
            descricao={
              situacao === 'aceite_vencido'
                ? 'Toda avaliadora acionada aceitou dentro do prazo, ou ainda está no prazo.'
                : 'Nenhuma avaliação acionada com a frente de avaliação aberta.'
            }
          />
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Fila da avaliação">
              <TableHead>
                <TableRow>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Operadora</TableCell>
                  <TableCell>Modalidade</TableCell>
                  <TableCell>Avaliadora</TableCell>
                  <TableCell>Acionada em</TableCell>
                  <TableCell>Prazo do aceite</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Turno</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fila.data.itens.map((a) => (
                  <TableRow key={a.casoId} hover>
                    <TableCell>
                      <LinkDoRoteador to={`/casos/${encodeURIComponent(a.casoId)}`}>{a.paciente}</LinkDoRoteador>
                      {a.nucleo !== null && (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {a.nucleo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{a.operadora}</TableCell>
                    <TableCell>{a.modalidade ?? '—'}</TableCell>
                    <TableCell>{a.avaliadora ?? '—'}</TableCell>
                    <TableCell>{a.acionadaEm !== null ? formatarDataHora(a.acionadaEm) : '—'}</TableCell>
                    <TableCell>{textoDoPrazo(a)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={COR_DA_SITUACAO[a.situacao]}
                        label={ROTULO_DA_SITUACAO[a.situacao]}
                      />
                    </TableCell>
                    <TableCell>{a.turno !== null ? ROTULO_DO_TURNO[a.turno] : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}
